// src/components/dashboard/ActiveSessionsWidget.js

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/dashboard-widgets.css';

const ActiveSessionsWidget = () => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { 
    filteredActiveStaff, 
    activeStaffLoading, 
    activeStaffError,
    departmentFilter,
    setDepartmentFilter,
    searchFilter,
    setSearchFilter,
    getActiveStaffStats,
    formatDuration,
    formatTime,
    adminClockOutStaff
  } = useDashboard();

  const { departments } = useDepartment();
  const { company } = useAuth();
  const [sortBy, setSortBy] = useState('clockIn');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showClockOutConfirm, setShowClockOutConfirm] = useState(null);
  const [clockOutLoading, setClockOutLoading] = useState(null);
  const [clockOutError, setClockOutError] = useState(null);

  // Get available departments for filtering
  const departmentOptions = useMemo(() => {
    if (!departments || departments.length === 0) return [];
    return departments.map(dept => ({
      id: dept.id,
      name: dept.name
    }));
  }, [departments]);

  // Sort active staff
  const sortedActiveStaff = useMemo(() => {
    const sorted = [...filteredActiveStaff].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.staffName.toLowerCase();
          bValue = b.staffName.toLowerCase();
          break;
        case 'department':
          aValue = a.departmentName.toLowerCase();
          bValue = b.departmentName.toLowerCase();
          break;
        case 'duration':
          aValue = a.sessionDuration;
          bValue = b.sessionDuration;
          break;
        case 'clockIn':
        default:
          aValue = a.clockIn;
          bValue = b.clockIn;
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredActiveStaff, sortBy, sortOrder]);

  const stats = getActiveStaffStats();

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Navigate to staff detail page with time management tab
  const handleManageTime = (staffId, e) => {
    e.stopPropagation();
    navigate(`/admin/staff/${staffId}?tab=timeManagement`);
  };

  // Navigate to staff detail page (general)
  const handleRowClick = (staffId) => {
    navigate(`/admin/staff/${staffId}`);
  };

  // Admin clock out with confirmation
  const handleAdminClockOut = (staff, e) => {
    e.stopPropagation();
    setClockOutError(null);
    setShowClockOutConfirm(staff);
  };

  // Close modal and reset its local state (used by cancel / × / overlay click)
  const closeClockOutModal = () => {
    if (clockOutLoading) return; // don't allow closing mid-request
    setShowClockOutConfirm(null);
    setClockOutError(null);
  };

  // Confirm admin clock out
  const confirmAdminClockOut = async (staff, withAdjustment = false) => {
    if (withAdjustment) {
      setShowClockOutConfirm(null);
      navigate(`/admin/staff/${staff.staffId}?tab=timeManagement&action=clockOutAdjust`);
      return;
    }

    try {
      setClockOutLoading(staff.staffId);
      setClockOutError(null);

      if (adminClockOutStaff) {
        await adminClockOutStaff(staff.staffId);
      } else {
        console.warn('adminClockOutStaff method not available in DashboardContext');
      }

      // Success — close the modal immediately.
      setShowClockOutConfirm(null);
    } catch (error) {
      console.error('Error clocking out staff:', error);
      setClockOutError(
        t('activeSessionsWidget.clockOutModal.error') ||
        'Failed to clock out staff member. Please try again.'
      );
    } finally {
      setClockOutLoading(null);
    }
  };

  const getStaffInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  };

  const getStatusBadge = (staff) => {
    if (staff.onBreak) {
      const breakDuration = staff.breakStart ? 
        Math.floor((new Date() - staff.breakStart) / (1000 * 60)) : 0;
      return (
        <span className={`status-badge break ${breakDuration > 120 ? 'long-break' : ''}`}>
          {t('activeSessionsWidget.status.onBreak')} {breakDuration > 0 && `(${formatDuration(breakDuration)})`}
        </span>
      );
    }
    return <span className="status-badge working">{t('activeSessionsWidget.status.working')}</span>;
  };

  const getSessionAlert = (staff) => {
    if (staff.isLongSession) {
      return (
        <div className="session-alert long-session">
          ! {t('activeSessionsWidget.alerts.longSessionAlert')} ({formatDuration(staff.sessionDuration)})
        </div>
      );
    }
    return null;
  };

  if (activeStaffLoading) {
    return (
      <div className="active-sessions-widget">
        <div className="widget-header">
          <h3>{t('activeSessionsWidget.title')}</h3>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('activeSessionsWidget.loading.loadingSessions')}</p>
        </div>
      </div>
    );
  }

  if (activeStaffError) {
    return (
      <div className="active-sessions-widget">
        <div className="widget-header">
          <h3>{t('activeSessionsWidget.title')}</h3>
        </div>
        <div className="error-state">
          <p className="error-message">× {activeStaffError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="active-sessions-widget">
      <div className="widget-header">
        <h3>{t('activeSessionsWidget.title')}</h3>
        <div className="stats-summary">
          <span className="stat">
            <strong>{stats.total}</strong> {t('activeSessionsWidget.stats.total')}
          </span>
          <span className="stat working">
            <strong>{stats.working}</strong> {t('activeSessionsWidget.stats.working')}
          </span>
          <span className="stat break">
            <strong>{stats.onBreak}</strong> {t('activeSessionsWidget.stats.onBreak')}
          </span>
          {stats.longSessions > 0 && (
            <span className="stat alert">
              <strong>{stats.longSessions}</strong> {t('activeSessionsWidget.stats.longSessions')}
            </span>
          )}
        </div>
      </div>

      <div className="widget-filters">
        <div className="filter-group">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="department-filter"
          >
            <option value="all">{t('activeSessionsWidget.filters.allDepartments')}</option>
            {departmentOptions.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder={t('activeSessionsWidget.filters.searchByName')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="search-filter"
          />
        </div>
      </div>

      {sortedActiveStaff.length === 0 ? (
        <div className="empty-state">
          {filteredActiveStaff.length === 0 && !searchFilter && departmentFilter === 'all' ? (
            <>
              <div className="empty-icon">○</div>
              <h4>{t('activeSessionsWidget.emptyStates.noActiveSessions')}</h4>
              <p>{t('activeSessionsWidget.emptyStates.noStaffClockedIn')}</p>
            </>
          ) : (
            <>
              <div className="empty-icon">∅</div>
              <h4>{t('activeSessionsWidget.emptyStates.noResults')}</h4>
              <p>{t('activeSessionsWidget.emptyStates.noMatchingFilters')}</p>
              <button 
                onClick={() => {
                  setDepartmentFilter('all');
                  setSearchFilter('');
                }}
                className="clear-filters-btn"
              >
                {t('activeSessionsWidget.actions.clearFilters')}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="active-sessions-table">
          <div className="table-header">
            <div 
              className={`header-cell staff-member-header sortable ${sortBy === 'name' ? 'active' : ''}`}
              onClick={() => handleSort('name')}
            >
              {t('activeSessionsWidget.tableHeaders.staffMember')} {getSortIcon('name')}
            </div>
            <div 
              className={`header-cell sortable ${sortBy === 'department' ? 'active' : ''}`}
              onClick={() => handleSort('department')}
            >
              {t('activeSessionsWidget.tableHeaders.department')} {getSortIcon('department')}
            </div>
            <div 
              className={`header-cell sortable ${sortBy === 'clockIn' ? 'active' : ''}`}
              onClick={() => handleSort('clockIn')}
            >
              {t('activeSessionsWidget.tableHeaders.clockIn')} {getSortIcon('clockIn')}
            </div>
            <div 
              className={`header-cell sortable ${sortBy === 'duration' ? 'active' : ''}`}
              onClick={() => handleSort('duration')}
            >
              {t('activeSessionsWidget.tableHeaders.duration')} {getSortIcon('duration')}
            </div>
            <div className="header-cell">{t('activeSessionsWidget.tableHeaders.status')}</div>
            <div className="header-cell admin-actions-header">{t('activeSessionsWidget.tableHeaders.adminActions')}</div>
          </div>

          <div className="table-body">
            {sortedActiveStaff.map((staff) => (
              <div 
                key={staff.staffId} 
                className={`table-row clickable ${staff.isLongSession ? 'long-session-row' : ''}`}
                onClick={() => handleRowClick(staff.staffId)}
              >
                <div 
                  className="cell staff-name-cell" 
                  data-label={t('activeSessionsWidget.tableHeaders.staffMember')}
                >
                  <div className="staff-info">
                    <span className="name">{staff.staffName}</span>
                    {getSessionAlert(staff)}
                  </div>
                </div>
                
                <div 
                  className="cell department"
                  data-label={t('activeSessionsWidget.tableHeaders.department')}
                >
                  {staff.departmentName}
                </div>
                
                <div 
                  className="cell clock-in"
                  data-label={t('activeSessionsWidget.tableHeaders.clockIn')}
                >
                  <div className="time-info">
                    <span className="time">{formatTime(staff.clockIn)}</span>
                    <span className="date">
                      {staff.clockIn?.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
                
                <div 
                  className="cell duration"
                  data-label={t('activeSessionsWidget.tableHeaders.duration')}
                >
                  <span className={`duration ${staff.isLongSession ? 'long' : ''}`}>
                    {formatDuration(staff.sessionDuration)}
                  </span>
                </div>
                
                <div 
                  className="cell status"
                  data-label={t('activeSessionsWidget.tableHeaders.status')}
                >
                  {getStatusBadge(staff)}
                </div>

                <div 
                  className="cell admin-actions-cell"
                  data-label={t('activeSessionsWidget.tableHeaders.adminActions')}
                >
                  <div className="action-badges">
                    <span
                      onClick={(e) => handleAdminClockOut(staff, e)}
                      className="action-badge clock-out"
                      title={t('activeSessionsWidget.actions.clockOut')}
                    >
                      {t('activeSessionsWidget.actions.clockOut')}
                    </span>
                    <span
                      onClick={(e) => handleManageTime(staff.staffId, e)}
                      className="action-badge manage"
                      title={t('activeSessionsWidget.actions.manage')}
                    >
                      {t('activeSessionsWidget.actions.manage')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedActiveStaff.length > 0 && (
        <div className="widget-footer">
          <p className="summary-text">
            {t('activeSessionsWidget.summary.showing')} {sortedActiveStaff.length} {t('activeSessionsWidget.summary.of')} {filteredActiveStaff.length} {t('activeSessionsWidget.summary.activeSessions')}
            {(departmentFilter !== 'all' || searchFilter) && (
              <button 
                onClick={() => {
                  setDepartmentFilter('all');
                  setSearchFilter('');
                }}
                className="clear-filters-link"
              >
                {t('activeSessionsWidget.actions.clearFiltersLink')}
              </button>
            )}
          </p>
        </div>
      )}

      {/* Clock Out Confirmation Modal — redesigned, modern card style */}
      {showClockOutConfirm && (
        <div className="modal-overlay" onClick={closeClockOutModal}>
          <div
            className="modal-content clock-out-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header clock-out-confirm-header">
              <div className="clock-out-header-info">
                <div className={`clock-out-avatar ${showClockOutConfirm.onBreak ? 'on-break' : 'working'}`}>
                  {getStaffInitials(showClockOutConfirm.staffName)}
                </div>
                <div>
                  <h3>{t('activeSessionsWidget.clockOutModal.title')} {showClockOutConfirm.staffName}</h3>
                  {getStatusBadge(showClockOutConfirm)}
                </div>
              </div>
              <button 
                onClick={closeClockOutModal}
                className="modal-close"
                disabled={!!clockOutLoading}
                title={t('activeSessionsWidget.clockOutModal.cancel')}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {clockOutError && (
                <div className="clock-out-error-banner">
                  <span className="icon-error">!</span>
                  {clockOutError}
                </div>
              )}

              <div className="clock-out-session-card">
                <div className="clock-out-session-row">
                  <span className="label">{t('activeSessionsWidget.clockOutModal.department')}</span>
                  <span className="value">{showClockOutConfirm.departmentName}</span>
                </div>
                <div className="clock-out-session-row">
                  <span className="label">{t('activeSessionsWidget.clockOutModal.clockedInAt')}</span>
                  <span className="value">{formatTime(showClockOutConfirm.clockIn)}</span>
                </div>
                <div className="clock-out-session-row">
                  <span className="label">{t('activeSessionsWidget.clockOutModal.duration')}</span>
                  <span className={`duration ${showClockOutConfirm.isLongSession ? 'long' : ''}`}>
                    {formatDuration(showClockOutConfirm.sessionDuration)}
                  </span>
                </div>
              </div>
              
              <p className="clock-out-question">
                {t('activeSessionsWidget.clockOutModal.question')}
              </p>
            </div>
            
            <div className="modal-footer clock-out-confirm-footer">
              <button
                onClick={closeClockOutModal}
                className="btn-cancel"
                disabled={!!clockOutLoading}
              >
                {t('activeSessionsWidget.clockOutModal.cancel')}
              </button>
              <button
                onClick={() => confirmAdminClockOut(showClockOutConfirm, true)}
                className="btn-secondary"
                disabled={!!clockOutLoading}
              >
                {t('activeSessionsWidget.clockOutModal.clockOutAndAdjust')}
              </button>
              <button
                onClick={() => confirmAdminClockOut(showClockOutConfirm, false)}
                className="btn-primary"
                disabled={!!clockOutLoading}
              >
                {clockOutLoading === showClockOutConfirm.staffId ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('activeSessionsWidget.clockOutModal.processing')}
                  </>
                ) : (
                  t('activeSessionsWidget.clockOutModal.clockOutNow')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveSessionsWidget;